import { formatAmount, formatDate, formatDateTime, formatNumber, type Locale } from '@awj/i18n';
import type { Cell, CellObject, SheetData } from 'write-excel-file/universal';

/**
 * Tables, as Excel and PDF files.
 *
 * Both start from the same reads the tables make: the headings and statuses come out in the
 * language the member of staff is reading, and the file is exactly the list on screen — every
 * page of it, not just the twenty rows showing. The workbook is written here in the browser; the
 * PDF is laid out by the API, which shapes Arabic properly, from rows this file puts into words.
 *
 * One fact per column. A table cell stacks a name over an email to save width; a spreadsheet that
 * does the same cannot be sorted or filtered by either, which is what it is exported for.
 */

/**
 * How a column's values are written.
 *
 * Numbers, money and dates go in as real values with a display format, so a column of totals
 * can be summed and a column of dates sorted. Text is always text: a customer called "=1+1" is a
 * name in this file, never a formula.
 */
export type CellKind = 'text' | 'number' | 'money' | 'date' | 'dateTime';

export interface ExportColumn<Row> {
  header: string;
  kind?: CellKind;
  /** Dates are the API's ISO strings; everything else is the value to write. */
  value: (row: Row) => string | number | null | undefined;
}

export interface Workbook<Row> {
  /** The file's subject, as it goes in the name — `orders` becomes `awj-orders-2026-11-02.xlsx`. */
  file: string;
  sheet: string;
  columns: ExportColumn<Row>[];
  rows: Row[];
  locale: Locale;
}

/** A PDF as the API takes it: every cell already the text the panel would show. */
export interface PdfTable {
  columns: { header: string; alignEnd: boolean }[];
  rows: string[][];
}

/** The most rows the API returns in one page. */
const PAGE_SIZE = 100;

/** The API's own ceiling. Checked here too, so a long list is turned back before it is uploaded. */
export const MAX_PDF_ROWS = 20_000;

/**
 * Day before month, as the shop writes dates everywhere else. Numbers instead of month names
 * because Excel would print the month in its own language, whatever the panel's.
 */
const FORMATS: Record<Exclude<CellKind, 'text'>, string> = {
  number: '#,##0',
  money: '#,##0.00',
  date: 'dd/mm/yyyy',
  dateTime: 'dd/mm/yyyy hh:mm',
};

/** The header row in the brand's own browns: brass-100 under the brand ink, a brass-200 rule. */
const HEADER: CellObject = {
  fontWeight: 'bold',
  textColor: '#251810',
  backgroundColor: '#f4e9e2',
  bottomBorderStyle: 'thin',
  bottomBorderColor: '#e4d0c3',
};

/** Every page of a paged list, read in turn until the last one. */
export async function fetchAllPages<Row>(
  read: (page: number, pageSize: number) => Promise<{ items: Row[]; totalPages?: number }>,
): Promise<Row[]> {
  const rows: Row[] = [];

  for (let page = 1; ; page++) {
    const result = await read(page, PAGE_SIZE);
    rows.push(...result.items);

    if (result.items.length === 0 || page >= (result.totalPages ?? 1)) {
      return rows;
    }
  }
}

export function sheetData<Row>(columns: ExportColumn<Row>[], rows: Row[]): SheetData {
  return [
    columns.map((column) => ({ ...HEADER, value: column.header })),
    ...rows.map((row) => columns.map((column) => cell(column.kind ?? 'text', column.value(row)))),
  ];
}

/** Wide enough for the longest value in each column, within reason. */
export function columnWidths<Row>(columns: ExportColumn<Row>[], rows: Row[]): { width: number }[] {
  return columns.map((column) => {
    const longest = rows.reduce(
      (widest, row) => Math.max(widest, measure(column.kind ?? 'text', column.value(row))),
      column.header.length + 2,
    );

    return { width: Math.min(longest + 2, 60) };
  });
}

export async function downloadWorkbook<Row>({ file, sheet, columns, rows, locale }: Workbook<Row>): Promise<void> {
  // Loaded on the first export rather than with the panel: most sessions never make one.
  const { default: writeExcelFile } = await import('write-excel-file/universal');

  const blob = await writeExcelFile(sheetData(columns, rows), {
    sheet,
    columns: columnWidths(columns, rows),
    stickyRowsCount: 1,
    rightToLeft: locale === 'ar',
  }).toBlob();

  saveFile(blob, fileName(file, 'xlsx'));
}

/**
 * The table in words, as the PDF prints it — formatted exactly as the panel's own table formats
 * it, because a printed page is read, not sorted. Figures line up at the reading end.
 */
export function pdfTable<Row>(columns: ExportColumn<Row>[], rows: Row[], locale: Locale): PdfTable {
  return {
    columns: columns.map((column) => ({
      header: column.header,
      alignEnd: column.kind === 'number' || column.kind === 'money',
    })),
    rows: rows.map((row) => columns.map((column) => display(column.kind ?? 'text', column.value(row), locale))),
  };
}

/** `orders` and `pdf` become `awj-orders-2026-11-02.pdf`. */
export function fileName(file: string, extension: 'xlsx' | 'pdf'): string {
  return `awj-${file}-${today()}.${extension}`;
}

export function saveFile(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = name;
  link.click();

  // Not revoked straight away: a browser may still be reading the URL when `click()` returns.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function display(kind: CellKind, value: string | number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  switch (kind) {
    case 'number':
      return formatNumber(locale, Number(value));
    case 'money':
      // The currency is in the heading, so a column of totals is figures and nothing else.
      return formatAmount(Number(value), { grouped: true });
    case 'date':
      return formatDate(locale, String(value));
    case 'dateTime':
      return formatDateTime(locale, String(value));
    default:
      return String(value);
  }
}

function cell(kind: CellKind, value: string | number | null | undefined): Cell {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  switch (kind) {
    case 'text':
      return { value: String(value), type: String };
    case 'date':
    case 'dateTime':
      return { value: wallClock(value), type: Date, format: FORMATS[kind] };
    default:
      return { value: Number(value), type: Number, format: FORMATS[kind] };
  }
}

/**
 * The moment as the panel shows it.
 *
 * A spreadsheet has no time zones, only a wall-clock reading, and the library takes that reading
 * from the moment's UTC fields. An order placed at 01:30 in Dubai would land on the previous
 * evening — a different day from the one in the table it came from. Moving it by the browser's
 * own offset writes the time the panel displayed.
 */
function wallClock(value: string | number): Date {
  const moment = new Date(value);

  return new Date(moment.getTime() - moment.getTimezoneOffset() * 60_000);
}

function measure(kind: CellKind, value: string | number | null | undefined): number {
  if (value === null || value === undefined) {
    return 0;
  }

  switch (kind) {
    case 'date':
      return 10;
    case 'dateTime':
      return 16;
    case 'number':
    case 'money':
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: kind === 'money' ? 2 : 0 }).length;
    default:
      return Math.max(...String(value).split('\n').map((line) => line.length));
  }
}

function today(): string {
  // `en-CA` is the locale that writes a date as YYYY-MM-DD, which sorts in a downloads folder.
  return new Date().toLocaleDateString('en-CA');
}
